import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { FaComputer, FaPersonWalking } from "react-icons/fa6";
import { LuLoader, LuTrash } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import {
  delete_opportunity,
  get_opportunities,
  get_opportunities_type,
  update_opportunity_status,
} from "../../../services/apis/opportunity";
import { useAppDispatch, useAppSelector } from "../../../store";
import DataTable from "../../../component/element/table";
import { IOpportunity } from "../../../store/types/opportunity";
import ItemAction from "../../../component/ui/action/ItemAction";
import { Switch } from "../../../component/ui/Switch";
import PopConfirm from "../../../component/ui/PopConfirm";
import EditOportunity from "./EditOportunity";

const OpportunityTable = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [editingOpportunity, setEditingOpportunity] =
    React.useState<IOpportunity | null>(null);
  const opportuities = useAppSelector(
    ({ opportunity }) => opportunity.opportunities,
  );
  const opportunityRows = Array.isArray(opportuities) ? opportuities : [];
  const opportunityTypes = useAppSelector(
    ({ opportunity }) => opportunity.opportunity_types,
  );
  const opportunityTypesById = React.useMemo(
    () =>
      new Map(
        opportunityTypes.map((opportunityType) => [
          String(opportunityType.id),
          opportunityType.name,
        ]),
      ),
    [opportunityTypes],
  );

  const loadingOpportunityId = useAppSelector(
    ({ opportunity }) => opportunity.loadingOpportunityId,
  );
  const updatingOpportunityId = useAppSelector(
    ({ opportunity }) => opportunity.updatingOpportunityId,
  );
  const loading = useAppSelector(({ opportunity }) => opportunity.loading);
  const { limit, offset, current_page, last_page } = useAppSelector(
    ({ opportunity }) => opportunity,
  );
  React.useEffect(() => {
    get_opportunities(dispatch, limit, offset);
  }, [dispatch, limit, offset]);

  React.useEffect(() => {
    get_opportunities_type(dispatch);
  }, [dispatch]);

  const columns: ColumnDef<IOpportunity>[] = [
    {
      accessorKey: "opportunity_image",
      header: "Image",
      cell: ({ getValue }) => {
        const image = getValue() as string;
        return (
          <img
            loading="lazy"
            src={image}
            alt={image}
            className="w-11 h-auto object-cover rounded-lg"
          />
        );
      },
    },
    {
      accessorKey: "opportunity_name",
      header: "Name",
      cell: ({ getValue }) => {
        const name = getValue() as string;
        return <span className="capitalize">{name}</span>;
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => {
        const typeName =
          opportunityTypesById.get(String(row.original.opportunity_type)) ??
          row.original.type?.name;
        const normalizedTypeName = typeName?.toLowerCase();
        return (
          <span className={`rounded-md flex items-center`}>
            {typeName && (
              <div
                className={`flex items-center gap-2 text-xs font-medium px-2 py-1 rounded-full capitalize ${
                  normalizedTypeName === "physical"
                    ? "bg-blue-50 text-blue-800"
                    : normalizedTypeName === "online"
                      ? "bg-yellow-50 text-yellow-800"
                      : "bg-gray-100 text-gray-700"
                }`}
              >
                <span>
                  {normalizedTypeName === "physical" ? (
                    <FaPersonWalking />
                  ) : normalizedTypeName === "online" ? (
                    <FaComputer />
                  ) : null}
                </span>
                <span>{typeName}</span>
              </div>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const { id, opportunity_name, status } = row.original;
        const isActive = status === "1" || status === 1 || status === true;
        const isUpdating = updatingOpportunityId === id;
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={isActive}
              disabled={updatingOpportunityId !== null}
              aria-label={`${isActive ? "Deactivate" : "Activate"} ${opportunity_name}`}
              onCheckedChange={() =>
                update_opportunity_status(id, dispatch, status)
              }
            />
            {isUpdating && (
              <LuLoader
                size={16}
                className="animate-spin text-gray-500"
                aria-label="Updating status"
              />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "id",
      header: "Action",
      cell: ({ row }) => {
        const { id } = row.original;
        return (
          <div className="flex items-center gap-3">
            <ItemAction
              show_delete={false}
              onEdit={() => setEditingOpportunity(row.original)}
              onView={() => navigate(`/opportunity/${id}`)}
            />
            <PopConfirm
              title="Delete this opportunity?"
              description="This action cannot be undone."
              confirmText="Delete"
              onConfirm={() => delete_opportunity(id, dispatch)}
            >
              <button
                type="button"
                disabled={loadingOpportunityId === id}
                aria-label={`Delete ${row.original.opportunity_name}`}
                className="bg-red-50 hover:bg-red-300 text-red-800 p-2 rounded-md disabled:opacity-50"
              >
                {loadingOpportunityId === id ? (
                  <LuLoader size={18} className="animate-spin" />
                ) : (
                  <LuTrash size={18} />
                )}
              </button>
            </PopConfirm>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {loading && opportunityRows.length === 0 ? (
        <div className="py-12 text-center text-gray-500">
          Loading opportunities...
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={opportunityRows}
          current_page={current_page}
          last_page={last_page}
          onNext={(page) =>
            get_opportunities(dispatch, limit, (page - 1) * limit)
          }
          onPrev={(page) =>
            get_opportunities(dispatch, limit, (page - 1) * limit)
          }
        />
      )}
      {editingOpportunity && (
        <EditOportunity
          key={editingOpportunity.id}
          show
          opportunity={editingOpportunity}
          onHandler={() => setEditingOpportunity(null)}
        />
      )}
    </>
  );
};

export default OpportunityTable;
